import React, {
  useMemo,
  useRef,
  useState,
  useCallback,
  useEffect,
} from "react";
import {
  useMultiPageLessonStore,
  usePageByIndex,
  useCurrentPage,
  useBlock,
  useFeedbackBlock,
} from "../../store/multiPageLessonStore";
import {
  useWebSocketSubscription,
  WebSocketEventType,
} from "../../context/SprintWebSocketContext";
import ReactMarkdown from "react-markdown";
import { createMarkdownComponents } from "../../components/markdown/MarkdownComponents";
import { css } from "@emotion/css";
import { LessonNavigationButtons } from "./OriginalLessonPage/LessonNavigationButtons";
import { FeedbackModal, PullUpStrap } from "./OriginalLessonPage/FeedbackModal";
import { MCQBlockRenderer } from "./OriginalLessonPage/MCQBlockRenderer";
import { TextInputQuestionBlockRenderer } from "./OriginalLessonPage/TextInputQuestionBlockRenderer";

// Component for rendering individual blocks

const imageStyle = css`
  max-width: 100%;
  border-radius: 0.5rem;
  margin: 1rem 0;
`;

// Now accepts pageIndex and fieldName instead of block directly
// This allows it to subscribe only to the specific block it needs to render
interface BlockRendererProps {
  pageIndex: number;
  fieldName: string;
  showFeedbackModal?: (namespace: string) => void;
}

const BlockRenderer: React.FC<BlockRendererProps> = ({
  pageIndex,
  fieldName,
  showFeedbackModal,
}) => {
  // Use the fine-grained useBlock hook to only re-render when this specific block changes
  const block = useBlock(pageIndex, fieldName);

  // Create a ref to store citation mapping for sequential numbering
  const citationMapRef = useRef<Map<string, number>>(new Map());

  // Early return if block isn't loaded yet with a nice loading state
  if (!block) {
    return <div></div>;
  }

  switch (block.block_type) {
    case "markdown_content":
      return (
        <div key={block.block_id}>
          <ReactMarkdown
            components={createMarkdownComponents(citationMapRef.current)}
          >
            {block.content.raw_markdown}
          </ReactMarkdown>
        </div>
      );

    case "image_content":
      return (
        <div key={block.block_id}>
          {block.content?.url && (
            <div>
              <img
                src={block.content.url}
                alt="Lesson illustration"
                className={imageStyle}
              />
            </div>
          )}
        </div>
      );

    case "mcq":
      // Pass the callback to show feedback in the modal
      return (
        <MCQBlockRenderer
          block={block}
        />
      );
      
    case "text_input_question":
      return (
        <TextInputQuestionBlockRenderer
          block={block}
        />
      );

    default:
      return (
        <div key={block.block_id}>
          <p>Unsupported block type: {block.block_type}</p>
          <pre>{JSON.stringify(block, null, 2)}</pre>
        </div>
      );
  }
};

// Component for rendering a single page
interface PageRendererProps {
  pageIndex: number;
}

export const PageRenderer: React.FC<PageRendererProps> = ({ pageIndex }) => {
  // State for feedback modal
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [currentFeedbackNamespace, setCurrentFeedbackNamespace] = useState<
    string | null
  >(null);
  const [feedbackAvailable, setFeedbackAvailable] = useState(false);

  // Get feedback block for the current namespace
  const currentFeedbackBlock = useFeedbackBlock(currentFeedbackNamespace || "");

  // Function to show feedback modal
  const showFeedbackModal = useCallback((namespace: string) => {
    setCurrentFeedbackNamespace(namespace);
    setFeedbackModalOpen(true);
    setFeedbackAvailable(true);
  }, []);

  // Add non-schema blocks listener for feedback blocks
  const { updateFeedbackBlock } = useMultiPageLessonStore();

  useWebSocketSubscription(
    (message) =>
      message.event_type === WebSocketEventType.NON_SCHEMA_BLOCKS_UPDATE,
    (message) => {
      console.log("🎉🎉🎉🎉🎉🎉🎉🎉🎉🎉🎉");
      console.log("Received non-schema blocks update:", message);

      // If the message contains blocks, process them
      if (message.blocks && Array.isArray(message.blocks)) {
        // Process each block that has metadata.feedback_for_block_key
        message.blocks.forEach((block) => {
          // Only process blocks that have metadata and are metadata complete
          if (
            block.metadata &&
            block.is_metadata_complete &&
            block.metadata.feedback_for_block_key
          ) {
            const namespace = block.metadata.feedback_for_block_key;

            // If the block has a feedback_for_block_key in metadata, store it in our feedbackBlocks Map
            if (namespace && typeof namespace === "string") {
              console.log(
                `Updating feedback block for namespace: ${namespace}`,
                block
              );
              updateFeedbackBlock(namespace, block);
            }
          }
        });
      }
    }
  );

  // Use the optimized usePageByIndex hook with memoized selector
  // This component will only re-render when the page metadata changes
  const pageConfig = usePageByIndex(pageIndex);

  // Use the useMemo hook to memoize the field names and block IDs mapping
  // Get ordered field names from schema rather than sorting on every render
  const { fieldNames, blockIdsByField } = useMemo(() => {
    if (!pageConfig?.blocks)
      return { fieldNames: [], blockIdsByField: new Map() };

    // Create a map of field names to block IDs for stable keys
    const blockIdMap = new Map<string, string>();
    Object.entries(pageConfig.blocks).forEach(([field, block]) => {
      blockIdMap.set(field, block.block_id);
    });

    // First try to use the field order from the page's schema definition
    // This avoids the O(n log n) sort operation on every render
    let names: string[];
    const orderedFields = pageConfig.orderedFields ?? [];

    if (orderedFields.length > 0) {
      // Filter to only include fields that exist in blocks
      names = orderedFields.filter((name) => name in pageConfig.blocks);
    } else {
      // Fallback to object keys (unordered) if no schema ordering available
      names = Object.keys(pageConfig.blocks);
    }

    return { fieldNames: names, blockIdsByField: blockIdMap };
  }, [pageConfig]);

  if (!pageConfig) {
    return (
      <div>
        <div>
          <p>Loading page {pageIndex + 1}...</p>
        </div>
      </div>
    );
  }

  // Handle closing the feedback modal
  const handleCloseFeedbackModal = () => {
    // Just close the modal but keep feedback available for the pull-up strap
    setFeedbackModalOpen(false);
    // We maintain feedbackAvailable=true so the strap will show
  };

  // Handle continue button in the modal
  const handleFeedbackContinue = () => {
    setFeedbackModalOpen(false);

    // Use small delay to allow animation to finish
    setTimeout(() => {
      // Reset for next use
      setCurrentFeedbackNamespace(null);
    }, 300);
  };

  return (
    <div>
      <div>
        {/* Render each block with its own BlockRenderer that has fine-grained updates */}
        {fieldNames.map((fieldName) => {
          // Use stable block_id if available, fallback to field name
          const blockId = blockIdsByField.get(fieldName) || fieldName;
          return (
            <div key={blockId}>
              <BlockRenderer 
                pageIndex={pageIndex} 
                fieldName={fieldName} 
                showFeedbackModal={showFeedbackModal}
              />
            </div>
          );
        })}
      </div>

      {/* Navigation */}
      <LessonNavigationButtons
        pageConfig={pageConfig}
        currentPageIndex={pageIndex}
        onShowFeedback={showFeedbackModal}
      />

      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={feedbackModalOpen}
        onClose={handleCloseFeedbackModal}
        feedbackBlock={currentFeedbackBlock}
        onContinue={handleFeedbackContinue}
      />

      {/* Pull-up strap for reopening feedback */}
      <PullUpStrap
        feedbackAvailable={feedbackAvailable}
        feedbackModalOpen={feedbackModalOpen}
        setFeedbackModalOpen={setFeedbackModalOpen}
      />
    </div>
  );
};
